import type { RoleDefinition } from '../types';

//  Reusable scope groups
const baseScopes = ['dashboard', 'style', 'item', 'report', 'sample-requests'];
const extendedScopes = ['style/workflow', 'storybook', 'test'];
const fullCommonScopes = [...baseScopes, ...extendedScopes];

const supplierScopes = ['suppliers', 'purchase-order', 'supplier', 'direct-suppliers', 'brands'];
const adminScopes = [...fullCommonScopes, 'admin'];
const marketingScopes = [...fullCommonScopes, 'marketing'];
const orderScopes = [...fullCommonScopes, 'order'];
const analyticsScopes = [...fullCommonScopes, 'analytics'];
const complianceScopes = [...fullCommonScopes, 'compliance', ...supplierScopes];
const complianceAdminScopes = [...adminScopes, 'compliance', ...supplierScopes];
const personalSettingsScope = ['personal-settings'];
const settingsScope = ['settings'];
const zapierScopes = ['zapier'];

const publicScope = ['login', 'supplier/signup', 'supplier/verify'];
const exampleScope = ['example'];

// DL-60478 -- the organization's own D2 migration scheduling page (/d2-migration), Company
// Admin only. `checkAccess` matches a scope against a page with
// `scope.startsWith(page) || page.startsWith(scope)`, so a bare 'migration' scope would
// collide with any 'migration*' page; the `d2-` prefix avoids that and the route maps to it
// through ROUTE_SCOPE_OVERRIDES rather than deriving the scope from the pathname.
const migrationScope = ['d2-migration'];

//  Helper: uniform read/write access
const access = (scopes: string[]) => [
  { verb: 'read' as const, scopes },
  { verb: 'write' as const, scopes },
];

// Helper: read/write with different scopes
const mixedAccess = (readScopes: string[], writeScopes: string[]) => [
  { verb: 'read' as const, scopes: readScopes },
  { verb: 'write' as const, scopes: writeScopes },
];

// Roles Definition
export const roles: RoleDefinition[] = [
  { role: 'anonymous', access: access([...publicScope]) },
  {
    role: 'Company User',
    access: access([
      'taskboard',
      ...baseScopes,
      ...publicScope,
      ...personalSettingsScope,
      ...exampleScope,
      ...zapierScopes,
    ]),
  },
  {
    role: 'Company Admin',
    access: access([
      'taskboard',
      ...adminScopes,
      ...publicScope,
      ...personalSettingsScope,
      ...exampleScope,
      ...supplierScopes,
      ...zapierScopes,
      ...migrationScope,
    ]),
  },
  {
    role: 'Test User',
    access: mixedAccess(
      ['taskboard', 'dashboard', 'style/workflow', 'storybook', 'supplier', 'test', ...publicScope],
      [...publicScope]
    ),
  },
  {
    role: 'Test Admin',
    access: access([
      'admin',
      ...fullCommonScopes,
      'style/workflow',
      ...publicScope,
      'suppliers',
      'purchase-order',
      'supplier',
    ]),
  },
  {
    role: 'Supplier Admin',
    access: mixedAccess(
      ['storybook', 'test', ...supplierScopes, ...personalSettingsScope, ...settingsScope],
      [...supplierScopes, ...personalSettingsScope, ...settingsScope]
    ),
  },
  {
    // dl-60443: Supplier User may view Settings / company info but not edit (Supplier Admin only).
    role: 'Supplier User',
    access: mixedAccess(
      ['storybook', 'test', ...supplierScopes, ...personalSettingsScope, ...settingsScope],
      [...supplierScopes, ...personalSettingsScope]
    ),
  },
  {
    role: 'Price',
    access: access([...fullCommonScopes, 'style/workflow']),
  },
  {
    role: 'Analytics',
    access: access([...analyticsScopes]),
  },
  {
    role: 'Batch Modifications',
    access: access([...fullCommonScopes, 'style/workflow']),
  },
  {
    role: 'Care Instructions',
    access: access([...fullCommonScopes, 'style/workflow']),
  },
  {
    role: 'Color Admin',
    access: access([...adminScopes, 'style/workflow']),
  },
  {
    role: 'Compliance Admin',
    access: access([...complianceAdminScopes]),
  },
  {
    role: 'Compliance User',
    access: access([...complianceScopes]),
  },
  {
    role: 'Integrator',
    access: access([...fullCommonScopes, 'style/workflow']),
  },
  {
    role: 'Marketing',
    access: access([...marketingScopes]),
  },
  {
    role: 'Order',
    access: access([...orderScopes]),
  },
  {
    role: 'SKU-Barcodes',
    access: access([...fullCommonScopes, 'style/workflow']),
  },
  {
    role: 'Supplier Manager',
    access: access([...adminScopes, 'style/workflow', ...supplierScopes]),
  },
];
